/**
 * AnalysisLoadingSkeleton — layout-preserving pulse skeleton while analysis is
 * queued or running. Mirrors the approximate structure of AnalysisPanel.
 *
 * Sprint 2.5: accepts currentRound + currentTool from the agentic backend.
 * When provided, renders a live caption above the skeleton so engineers can see
 * which tool Claude is calling right now.
 */

import { Box, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';

export interface AnalysisLoadingSkeletonProps {
  /** Agentic: which round Claude is currently processing (null = not yet started). */
  currentRound?: number | null;
  /** Agentic: the tool being called right now (null = not yet started). */
  currentTool?: string | null;
}

export function AnalysisLoadingSkeleton({
  currentRound,
  currentTool,
}: AnalysisLoadingSkeletonProps) {
  const hasRoundInfo = currentRound != null && currentTool != null;

  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label="Analysis in progress"
      aria-live="polite"
    >
      {/* Agentic round indicator — only shown when the backend provides round context */}
      {hasRoundInfo && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          {/* Spinner with round number overlaid */}
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress size={32} thickness={3} />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontSize: 10, lineHeight: 1 }}
              >
                {currentRound}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: 'monospace' }}
          >
            Round {currentRound} — calling {currentTool}…
          </Typography>
        </Stack>
      )}

      <Stack spacing={3}>
        {/* Header row: severity badge + action chip + caption */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="rounded" width={120} height={28} />
          <Skeleton variant="rounded" width={100} height={28} />
          <Skeleton variant="text" width={220} height={20} />
        </Stack>

        {/* Summary section */}
        <Box>
          <Skeleton variant="text" width={80} height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="92%" />
          <Skeleton variant="text" width="85%" />
        </Box>

        {/* Impact section */}
        <Box>
          <Skeleton variant="text" width={64} height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="78%" />
        </Box>

        {/* Fix snippet block */}
        <Box>
          <Skeleton variant="text" width={120} height={24} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" width="100%" height={96} />
        </Box>

        {/* Related services chip row */}
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={80} height={28} />
          <Skeleton variant="rounded" width={96} height={28} />
          <Skeleton variant="rounded" width={64} height={28} />
        </Stack>
      </Stack>
    </Box>
  );
}
